import { i18n } from "@/plugin/vue-i18n";
import { CONDITIONAL_OPERATIONS } from "@/util/constants";
import { dateFormatForDayjs, dateFormatForUI, standardFields } from "@/util/methods";
import dayjs from "dayjs";
import { h, VNode } from "vue";

const { t } = i18n.global;

export interface FieldLike {
  id?: string;
  tag?: string;
  hiddenFieldQueryKey?: string;
  type?: string;
  dataType?: string;
}

export interface ConditionLike {
  rangeStart?: string | number | null | undefined;
  rangeEnd?: string | number | null | undefined;
  dateFormat?: string;
}

/**
 * Resolve an icon VNode for a given field. Matches by tag first, then by type when id exists,
 * and finally by hiddenFieldQueryKey when there is no id. Includes a special case for phone.
 */
export const getOptionIconVNode = (field?: FieldLike): VNode | null => {
  try {
    for (const groupKey of Object.keys(standardFields as any)) {
      const group = (standardFields as any)[groupKey]?.group;
      if (!Array.isArray(group)) {
        continue;
      }

      const found =
        (field?.tag ? group.find((item: any) => item?.tag === field.tag) : undefined) ||
        (field?.id && field?.type
          ? group.find((item: any) => item?.type === field.type) ||
            (field?.type === "phone" || field?.dataType === "PHONE"
              ? group.find((item: any) => item?.tag === "phone")
              : undefined)
          : undefined) ||
        (!field?.id && field?.hiddenFieldQueryKey
          ? group.find((item: any) => item?.hiddenFieldQueryKey === field.hiddenFieldQueryKey)
          : undefined);

      if (found?.icon) {
        const isRating =
          field?.type === "rating" || field?.tag === "rating" || field?.dataType === "RATING";
        let produced: any =
          typeof found.icon === "function" ? (found.icon as any)() : (found.icon as any);
        if (!produced) {
          return null;
        }

        // Some icons (e.g., rating) may return an array/fragment – choose the primary icon
        if (Array.isArray(produced)) {
          // Prefer the last non-null node (commonly the actual icon component)
          produced = produced.filter(Boolean).pop();
          if (!produced) {
            return null;
          }
        }

        // Normalize <img> nodes with consistent sizing
        if ((produced as any).type === "img") {
          const src = (produced as any)?.props?.src;
          return h("img", {
            src,
            style: {
              width: isRating ? "32px" : "24px",
              height: isRating ? "32px" : "24px",
              objectFit: "contain",
            },
          });
        }

        // Preserve original props while enforcing size
        const vnodeType = (produced as any).type || produced;
        const originalProps = ((produced as any).props || {}) as Record<string, any>;
        const mergedStyle = {
          ...(originalProps.style || {}),
          width: isRating ? "32px" : "24px",
          height: isRating ? "32px" : "24px",
        } as Record<string, any>;
        return h(vnodeType as any, { ...originalProps, style: mergedStyle });
      }
    }
  } catch {}
  return null;
};

/**
 * Build shortcuts for date range picker.
 */
export const dateRangeShortcuts = (): Record<string, [number, number]> => {
  const endOfToday = dayjs().endOf("day").valueOf();
  return {
    [t("common.today")]: [dayjs().startOf("day").valueOf(), endOfToday],
    [t("common.thisWeek")]: [dayjs().startOf("week").valueOf(), dayjs().endOf("week").valueOf()],
    [t("common.last7Days")]: [dayjs().subtract(6, "day").startOf("day").valueOf(), endOfToday],
    [t("common.thisMonth")]: [dayjs().startOf("month").valueOf(), dayjs().endOf("month").valueOf()],
    [t("common.last30Days")]: [dayjs().subtract(29, "day").startOf("day").valueOf(), endOfToday],
  } as Record<string, [number, number]>;
};

/**
 * Resolve the UI date format for a selected field.
 */
// Find first matching element by hiddenFieldQueryKey across slides
const findElementByHiddenKey = (slides: any[], selectedField: string): any | null => {
  for (const slide of slides) {
    if (slide?.slideData && Array.isArray(slide.slideData)) {
      const found = slide.slideData.find(
        (item: any) => item?.hiddenFieldQueryKey === selectedField
      );
      if (found) {
        return found;
      }
    }
  }
  return null;
};

export const dateTypeFieldFormat = (store: any, selectedField?: string): string => {
  if (!selectedField) {
    return dateFormatForUI();
  }
  const dateElement = store.formId
    ? store.elements.find((item: any) => item?.hiddenFieldQueryKey === selectedField)
    : findElementByHiddenKey(store.slides, selectedField);
  return dateFormatForUI(dateElement?.format, dateElement?.separator);
};

/**
 * Resolve the Dayjs date format for a selected field.
 */
export const dateTypeFieldFormatDayjs = (store: any, selectedField?: string): string => {
  if (!selectedField) {
    return dateFormatForDayjs();
  }
  const dateElement = store.formId
    ? store.elements.find((item: any) => item?.hiddenFieldQueryKey === selectedField)
    : findElementByHiddenKey(store.slides, selectedField);
  return dateFormatForDayjs(dateElement?.format, dateElement?.separator);
};
/**
 * Format to use when setting the datepicker value (parsing stored date strings).
 * Prefers the format stored on the condition so rendering stays correct if the
 * field's format in store is changed later.
 */
export const dateTypeFieldFormatDayjsForCondition = (
  store: any,
  condition: { dateFormat?: string; selectedField?: string | null }
): string => {
  if (condition?.dateFormat) {
    return condition.dateFormat;
  }
  return dateTypeFieldFormatDayjs(store, condition?.selectedField ?? undefined);
};

/**
 * Convert a stored date string to the numeric value UIDatepicker expects.
 * Uses storedFormat when provided (format from condition at save time) so the
 * datepicker value is correct even if the field's format in store changed.
 */
export const getDateTypeFieldValue = (
  store: any,
  selectedField: string | undefined,
  inputValue: any,
  storedFormat?: string
): number => {
  const fallbackFormat = dateTypeFieldFormatDayjs(store, selectedField);
  const format = storedFormat || fallbackFormat;
  const date = dayjs(inputValue, format).isValid()
    ? dayjs(inputValue, format).valueOf()
    : new Date().valueOf();
  return date;
};

/**
 * Determine whether to hide the value input for operations that don't need it.
 */
export const shouldHideInput = (selectedOperation?: string): boolean => {
  return (
    [
      CONDITIONAL_OPERATIONS.IS_FILLED,
      CONDITIONAL_OPERATIONS.IS_EMPTY,
      CONDITIONAL_OPERATIONS.IS_CHECKED,
      CONDITIONAL_OPERATIONS.IS_NOT_CHECKED,
    ] as string[]
  ).includes(selectedOperation as any);
};

/**
 * Render label for Include Start checkbox.
 */
export const includeStartText = (
  condition: ConditionLike,
  fieldType?: string,
  formatStr?: string
): string => {
  if (fieldType === "date") {
    if (!condition?.rangeStart) {
      return t("form.conditonalLogic.includeStart");
    }
    const displayFormat = formatStr || "YYYY-MM-DD";
    const parseFormat = condition?.dateFormat || displayFormat;
    const timestamp = dayjs(condition.rangeStart, parseFormat);
    const formatted = timestamp.isValid()
      ? timestamp.format(displayFormat)
      : String(condition.rangeStart);
    return t("form.conditonalLogic.includeValue", { value: formatted });
  }

  if (["numerical", "monetory", "score", "rating"].includes(fieldType || "")) {
    if (!condition?.rangeStart) {
      return t("form.conditonalLogic.includeStart");
    }
    const val =
      fieldType === "monetory" && !isNaN(parseFloat(String(condition.rangeStart)))
        ? parseFloat(String(condition.rangeStart)).toFixed(2)
        : String(condition.rangeStart);
    return t("form.conditonalLogic.includeValue", { value: val });
  }
  return t("form.conditonalLogic.includeStart");
};

/**
 * Render label for Include End checkbox.
 */
export const includeEndText = (
  condition: ConditionLike,
  fieldType?: string,
  formatStr?: string
): string => {
  if (fieldType === "date") {
    if (!condition?.rangeEnd) {
      return t("form.conditonalLogic.includeEnd");
    }
    const displayFormat = formatStr || "YYYY-MM-DD";
    const parseFormat = condition?.dateFormat || displayFormat;
    const timestamp = dayjs(condition.rangeEnd, parseFormat);
    const formatted = timestamp.isValid()
      ? timestamp.format(displayFormat)
      : String(condition.rangeEnd);
    return t("form.conditonalLogic.includeValue", { value: formatted });
  }
  if (["numerical", "monetory", "score", "rating"].includes(fieldType || "")) {
    if (!condition?.rangeEnd) {
      return t("form.conditonalLogic.includeEnd");
    }
    const val =
      fieldType === "monetory" && !isNaN(parseFloat(String(condition.rangeEnd)))
        ? parseFloat(String(condition.rangeEnd)).toFixed(2)
        : String(condition.rangeEnd);
    return t("form.conditonalLogic.includeValue", { value: val });
  }
  return t("form.conditonalLogic.includeEnd");
};
